import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const RANGE_FIELDS = [
  { key: "length", ar: "الطول" }, { key: "shoulder", ar: "الكتف" },
  { key: "chest", ar: "الصدر" }, { key: "sleeve", ar: "الكم" }, { key: "hip", ar: "الورك" },
];
const DEFAULT_RANGE = { length: { min: 100, max: 180 }, shoulder: { min: 30, max: 70 }, chest: { min: 70, max: 140 }, sleeve: { min: 40, max: 80 }, hip: { min: 80, max: 160 } };
const POLICY_KEYS = [
  { key: "terms", ar: "الشروط والأحكام" }, { key: "exchange", ar: "الاستبدال والاسترجاع" },
  { key: "privacy", ar: "الخصوصية" }, { key: "shipping", ar: "الشحن والتوصيل" },
];

export default function AdminSettings() {
  const [f, setF] = useState(null);
  const [ranges, setRanges] = useState(null);
  const [policies, setPolicies] = useState(null);
  const [story, setStory] = useState({ story_ar: "", story_en: "", story_enabled: true });
  const [savingStory, setSavingStory] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/settings").then((r) => {
    setF(r.data);
    setRanges({ ...DEFAULT_RANGE, ...(r.data.measurement_ranges || {}) });
    setPolicies(r.data.policies);
    setStory({ story_ar: r.data.story_ar || "", story_en: r.data.story_en || "", story_enabled: r.data.story_enabled !== false });
  });
  useEffect(() => { load(); }, []);
  if (!f) return <div className="p-8 text-text-muted">…</div>;

  const set = (k, v) => setF({ ...f, [k]: v });

  const saveContact = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { return_policy: f.return_policy, phone: f.phone, whatsapp: f.whatsapp, email: f.email, instagram: f.instagram, address: f.address });
      toast.success("تم حفظ بيانات التواصل");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const saveRanges = async () => {
    try {
      const clean = {};
      RANGE_FIELDS.forEach((rf) => { clean[rf.key] = { min: Number(ranges[rf.key].min), max: Number(ranges[rf.key].max) }; });
      await api.put("/admin/measurement-ranges", { measurement_ranges: clean });
      toast.success("تم حفظ نطاقات المقاسات");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const savePolicy = async (key) => {
    try { await api.put(`/admin/policies/${key}`, { content: policies[key].content }); toast.success("تم نشر نسخة جديدة"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveStory = async () => {
    setSavingStory(true);
    try {
      const { data } = await api.put("/admin/story", { story_ar: story.story_ar, story_en: story.story_en, story_enabled: story.story_enabled });
      setStory({ story_ar: data.story_ar || "", story_en: data.story_en || "", story_enabled: data.story_enabled !== false });
      toast.success("تم حفظ قصة رنف");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingStory(false); }
  };

  const field = (label, key, dir) => (
    <div><label className="text-xs text-text-muted">{label}</label>
      <input value={f[key] || ""} onChange={(e) => set(key, e.target.value)} dir={dir} data-testid={`setting-${key}`}
        className="w-full border border-border px-3 py-2.5 text-sm mt-1 focus:outline-none focus:ring-1 focus:ring-primary" /></div>
  );

  return (
    <div className="p-5 md:p-8 max-w-3xl space-y-6">
      <h1 className="font-display text-3xl">الإعدادات</h1>

      <div className="bg-white border border-border p-6 space-y-4">
        <h2 className="text-sm tracking-luxe uppercase text-accent">بيانات التواصل</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {field("الهاتف", "phone", "ltr")}{field("واتساب", "whatsapp", "ltr")}
          {field("البريد الإلكتروني", "email", "ltr")}{field("انستغرام", "instagram", "ltr")}
        </div>
        {field("العنوان", "address", "rtl")}
        <button onClick={saveContact} disabled={saving} data-testid="save-settings-btn" className="bg-primary text-primary-foreground px-8 py-3 text-sm disabled:opacity-50">حفظ</button>
      </div>

      <div className="bg-white border border-border p-6 space-y-4">
        <h2 className="text-sm tracking-luxe uppercase text-accent">نطاقات المقاسات المسموحة (سم)</h2>
        <div className="grid grid-cols-2 gap-4">
          {RANGE_FIELDS.map((rf) => (
            <div key={rf.key} className="flex items-center gap-2">
              <span className="text-sm w-16">{rf.ar}</span>
              <input type="number" value={ranges[rf.key].min} onChange={(e) => setRanges({ ...ranges, [rf.key]: { ...ranges[rf.key], min: e.target.value } })} data-testid={`range-${rf.key}-min`} className="w-20 border border-border px-2 py-1.5 text-sm" placeholder="أدنى" />
              <span className="text-text-muted">—</span>
              <input type="number" value={ranges[rf.key].max} onChange={(e) => setRanges({ ...ranges, [rf.key]: { ...ranges[rf.key], max: e.target.value } })} data-testid={`range-${rf.key}-max`} className="w-20 border border-border px-2 py-1.5 text-sm" placeholder="أعلى" />
            </div>
          ))}
        </div>
        <button onClick={saveRanges} data-testid="save-ranges-btn" className="bg-primary text-primary-foreground px-8 py-3 text-sm">حفظ النطاقات</button>
      </div>

      <div className="bg-white border border-border p-6 space-y-5">
        <h2 className="text-sm tracking-luxe uppercase text-accent">السياسات (تحرير ونشر نسخة جديدة)</h2>
        {POLICY_KEYS.map((p) => (
          <div key={p.key}>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm">{p.ar} <span className="text-[11px] text-text-muted">(نسخة {policies[p.key]?.version})</span></label>
              <button onClick={() => savePolicy(p.key)} data-testid={`save-policy-${p.key}`} className="text-xs bg-primary text-primary-foreground px-3 py-1.5">نشر</button>
            </div>
            <textarea value={policies[p.key]?.content || ""} onChange={(e) => setPolicies({ ...policies, [p.key]: { ...policies[p.key], content: e.target.value } })} rows={4} data-testid={`policy-${p.key}`}
              className="w-full border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary" />
          </div>
        ))}
      </div>
      <div className="bg-white border border-border p-6 space-y-4" data-testid="story-section">
        <div className="flex items-center justify-between">
          <h2 className="text-sm tracking-luxe uppercase text-accent">قصة رنف (تظهر في الصفحة الرئيسية)</h2>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={story.story_enabled} onChange={(e) => setStory({ ...story, story_enabled: e.target.checked })} data-testid="story-enabled" />
            {story.story_enabled ? "ظاهرة" : "مخفية"}
          </label>
        </div>
        <p className="text-[11px] text-text-muted">اكتبي كل فقرة في سطر. اتركي الحقل فارغاً لاستخدام النص الافتراضي الحالي. إخفاء القسم لا يحذف النص.</p>
        <div>
          <label className="text-xs text-text-muted">النص بالعربية</label>
          <textarea value={story.story_ar} onChange={(e) => setStory({ ...story, story_ar: e.target.value })} rows={6} dir="rtl" data-testid="story-ar"
            placeholder="اتركيه فارغاً لاستخدام النص الافتراضي" className="w-full border border-border px-3 py-2.5 text-sm mt-1 focus:outline-none focus:ring-1 focus:ring-primary" />
        </div>
        <div>
          <label className="text-xs text-text-muted">النص بالإنجليزية (English)</label>
          <textarea value={story.story_en} onChange={(e) => setStory({ ...story, story_en: e.target.value })} rows={6} dir="ltr" data-testid="story-en"
            placeholder="Leave empty to use the default text" className="w-full border border-border px-3 py-2.5 text-sm mt-1 focus:outline-none focus:ring-1 focus:ring-primary" />
        </div>
        <button onClick={saveStory} disabled={savingStory} data-testid="save-story-btn" className="bg-[#141110] text-white px-8 py-3 text-sm disabled:opacity-50">{savingStory ? "جارٍ الحفظ…" : "حفظ قصة رنف"}</button>
      </div>
    </div>
  );
}
